import {createConsumer, type Subscription} from '@rails/actioncable';
import camelcaseKeysDeep from 'camelcase-keys-deep';
import type {QueryReturnValue} from '@reduxjs/toolkit/query';
import {cleanRepairData} from '../../utils/clean-repair-data';
import {api} from './apiSlice';
import type {RepairApiResponseRaw, WebSocketEventRaw, WebSocketRepairDataRaw} from './types-api-raw';
import type {GetRepair, GroupData} from './types';

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * getRepair
     * * Takes a claim id and fetches the repair and group data
     * * Also sets up a websocket connection to listen for changes to the repair
     */
    getRepair: build.query<GetRepair, string | undefined>({
      async queryFn(claimId: string | undefined, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const getRepairResult = (await fetchWithBaseQuery(
          `/claims/${claimId}?tracking=true`
        )) as QueryReturnValue<RepairApiResponseRaw>;

        /* RTK Query -> If error field exists, then data is undefined. If error is undefined, data is guaranteed to exist */
        if (getRepairResult.error) return {error: getRepairResult.error};

        const repairData = getRepairResult.data!; // safely assert data field exists (since error is undefined)
        const {group, ...repair} = repairData;

        const cleanedRepair = cleanRepairData(camelcaseKeysDeep(repair));
        if (!cleanedRepair) {
          return {error: {status: 400, data: 'Repair not found'}};
        }

        return {
          data: {
            repair: cleanedRepair,
            group: camelcaseKeysDeep(group),
          },
        };
      },
      /**
       * onCacheEntryAdded handles web socket logic for the repair
       */
      async onCacheEntryAdded(claimId: string | undefined, {updateCachedData, cacheDataLoaded, cacheEntryRemoved}) {
        // Block ActionCable connection if claimId is missing or using mock testing data
        if (!claimId || claimId.startsWith('MOCK') || claimId.startsWith('TEST')) {
          await cacheDataLoaded;
          await cacheEntryRemoved;
          return;
        }

        const ws = createConsumer(import.meta.env.VITE_REACT_APP_STRIPE_HQ_WEBSOCKET_HOST);
        let channel: Subscription | undefined = undefined;

        try {
          // wait for the initial query to resolve before proceeding
          await cacheDataLoaded;

          // when data is received from the socket connection to the server,
          // update our queried repair with the received repair
          const received = (event: WebSocketEventRaw) => {
            // event.repair is from HQ jobs/broadcast/claim_tracking/claim_data.rb <--- repair tracking
            // event.location is from HQ jobs/broadcast/claim_tracking/location_data.rb <--- tech location tracking
            const newRepairData: WebSocketRepairDataRaw = JSON.parse(
              event.repair || JSON.stringify({lastLocation: event.location})
            );

            if ([newRepairData.id, newRepairData.lastLocation?.claim_id].includes(Number(claimId))) {
              const {group, ...repair} = newRepairData;
              const cleanedRepair = cleanRepairData(camelcaseKeysDeep(repair));
              if (!cleanedRepair) return;

              updateCachedData(() => {
                return {
                  repair: cleanedRepair,
                  group: camelcaseKeysDeep(group ?? {}) as GroupData,
                };
              });
            }
          };

          const connected = () => {
            if (channel) {
              channel.send({id: claimId, need_update: true});
            }
            console.warn(`Tracking Repair: ${claimId}`);
          };

          const disconnected = () => {
            console.warn(`Disconnected from tracking Repair: ${claimId}`);
          };

          const rejected = () => {
            console.warn('Websocket connection rejected.');
          };

          channel = ws.subscriptions.create(
            {channel: 'RepairTrackingChannel', id: claimId},
            {received, connected, disconnected, rejected}
          );
        } catch (error) {
          // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
          // in which case `cacheDataLoaded` will throw
          console.error('Error:', error);
        }

        // cacheEntryRemoved will resolve when the cache subscription is no longer active
        await cacheEntryRemoved;
        // perform cleanup steps once the `cacheEntryRemoved` promise resolves
        ws.disconnect();
      },
    }),
  }),
  overrideExisting: false,
});

export const {useGetRepairQuery} = extendedApi;
